import React, { useState } from 'react';
import { Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import api from '../../services/api'; // Adjust path as necessary

// Define the structure of the new reply object returned by the API
interface NewReply {
    id: number;
    content: string;
    createdAt: string;
    author: {
        id: number;
        name: string;
    };
    // include other fields if returned by your API
}

interface CreateReplyFormProps {
    topicId: number;
    onReplySubmitSuccess: (newReply: NewReply) => void; // Callback on success
    // Add onCancel or other props if needed
}

const CreateReplyForm: React.FC<CreateReplyFormProps> = ({ topicId, onReplySubmitSuccess }) => {
    const [content, setContent] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        if (!content.trim()) {
            setError('Reply content cannot be empty.');
            return;
        }

        setSubmitting(true);

        try {
            const response = await api.post<{ success: boolean; data: NewReply; message?: string }>(
                `/forum/topics/${topicId}/replies`,
                { content: content.trim() } // Send only content in the body
            );

            if (response.data.success) {
                setContent(''); // Clear the form
                onReplySubmitSuccess(response.data.data); // Pass the new reply data back
            } else {
                throw new Error(response.data.message || 'Failed to submit reply.');
            }
        } catch (err: any) {
            console.error("Error submitting reply:", err);
            setError(err.response?.data?.message || err.message || 'An error occurred while posting the reply.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card className="mt-4 shadow-sm border-0">
            <Card.Body>
                <Card.Title as="h5" className="mb-3">Add Your Reply</Card.Title>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="replyContent">
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Share your thoughts or expertise..."
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            disabled={submitting}
                            required
                        />
                         <Form.Text className="text-muted">
                            You can use Markdown for formatting (basic support).
                        </Form.Text>
                    </Form.Group>
                    <div className="d-flex justify-content-end">
                        <Button variant="primary" type="submit" disabled={submitting || !content.trim()}>
                            {submitting ? (
                                <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Posting...</>
                            ) : (
                                'Post Reply'
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default CreateReplyForm; 